import { FC, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import { Divider, ListItemIcon } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getOrganization, getUserInfo, getUserOrganizations } from 'redux/reducers/user/selectors';
import { useLocation, useNavigate } from 'react-router';
import CheckIcon from '@mui/icons-material/Check';
import {
	getNewMessageCountRequest,
	getOrganizationsRequest,
	setOrganization,
} from 'redux/reducers/user/reducer';
import { TOrganizationsRow } from 'redux/reducers/user/types';
import { getMerchantIdSuccess } from 'redux/reducers/auth/reducer';
import { nanoid } from '@reduxjs/toolkit';
import { TUserMenuProps } from './types';
import { NAVIGATE_USER_MENU } from '../constants';

export const UserMenu: FC<TUserMenuProps> = ({
	anchorElUser,
	handleCloseUserMenu,
	handleLogout,
}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	useEffect(() => {
		dispatch(getOrganizationsRequest());
	}, [dispatch, anchorElUser]);

	const userinfo = useSelector(getUserInfo);
	const organization = useSelector(getOrganization);
	const organizations = useSelector(getUserOrganizations);

	const handleChangeOrganization = (org: TOrganizationsRow) => {
		dispatch(getMerchantIdSuccess({ xMerchantId: org?.groupId }));
		dispatch(setOrganization(org));
		dispatch(getNewMessageCountRequest());
		navigate(`/dashboard`);
	};

	const stringAvatar = (name: string) => {
		if (!name) return {};
		const children = name.includes(' ')
			? `${name?.split(' ')?.[0]?.[0]}${name?.split(' ')?.[1]?.[0]}`
			: name?.[0];
		return {
			sx: {
				bgcolor: '#fff',
				height: '30px',
				width: '30px',
				fontSize: '1.0rem',
				borderRadius: '8px',
				color: '#016193',
				fontWeight: 600,
			},
			children,
		};
	};

	return (
		<Menu
			sx={{
				mt: '45px',
				borderRadius: '12px',
				'& .MuiMenu-paper': {
					borderRadius: '12px',
				},
			}}
			id="menu-appbar"
			anchorEl={anchorElUser}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			keepMounted
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={Boolean(anchorElUser)}
			onClose={handleCloseUserMenu}
		>
			<Box
				sx={{
					mt: '-8px',
					p: '14px 16px 14px 16px',
					minWidth: 280,
					bgcolor: '#016193',
					color: 'white',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'start' }}>
					<Avatar {...stringAvatar(userinfo?.name || '')} />
					<Box sx={{ pl: '10px' }}>
						<Typography textAlign="left" variant="h6">
							{organization?.groupName ?? ''}
						</Typography>
						<Typography textAlign="left" variant="body2" color="white">
							{userinfo?.email ?? ''}
						</Typography>
						<Typography textAlign="left" variant="h6" component="div">
							{userinfo?.name ?? ''}
						</Typography>
					</Box>
				</Box>{' '}
			</Box>
			{(organizations?.data?.length ?? 0) > 1 && (
				<Box>
					{organizations?.data?.map((item, index) => (
						<MenuItem
							key={nanoid()}
							value={index + 1}
							sx={{
								fontWeight: item.id === organization?.id ? 'bold' : 'normal',
								display: 'flex',
								justifyContent: 'space-between',
								background: item.id === organization?.id ? '#eee' : 'normal',
							}}
							onClick={() => handleChangeOrganization(item)}
						>
							{item.groupName}

							{item.id === organization?.id && <CheckIcon />}
						</MenuItem>
					))}
				</Box>
			)}
			<Divider
				variant="middle"
				sx={{
					marginTop: '10px',
					marginBottom: '10px',
				}}
			/>
			{NAVIGATE_USER_MENU.map((item) =>
				item.type === 'divider' ? (
					<Divider key={nanoid()} />
				) : (
					<MenuItem
						onClick={() => {
							navigate(item.path);
							// if (window.location.origin.includes('2024.easproject.com')) {
							// 	window.open(item.path, '_blank');
							// } else {
							// 	navigate(item.path);
							// }
						}}
						sx={{
							// mt: '10px',
							fontWeight: location.pathname === item.path ? 'bold' : 'normal',
							display: 'flex',
							justifyContent: 'space-between',
							background: location.pathname === item.path ? '#eee' : 'normal',
						}}
						key={nanoid()}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
							}}
						>
							<ListItemIcon
								sx={{
									fontWeight: location.pathname === item.path ? 'bold' : 'normal',
								}}
							>
								{item.icon}
							</ListItemIcon>
							{item.name}
							{item.new && (
								<Box
									sx={{
										color: 'white',
										fontSize: '0.65rem',
										fontWeight: 600,
										m: '-16px 0 0 3px',
										borderRadius: '12px',
										padding: '2px 4px',
										// height: '12px',
										lineHeight: '10px',
										backgroundColor: 'error.main',
									}}
								>
									New
								</Box>
							)}
						</Box>
						{location.pathname === item.path && <CheckIcon />}
					</MenuItem>
				),
			)}

			<Divider variant="middle" />
			<MenuItem onClick={handleLogout} key={nanoid()}>
				<ListItemIcon>
					<Logout fontSize="small" />
				</ListItemIcon>
				<Typography textAlign="center">Logout</Typography>
			</MenuItem>
		</Menu>
	);
};
